//* 组件库
import type { UiSelect, UiTypes, UiModal, UiFormItem } from "various-ui";
import { $loading, $message, utility } from "various-ui";
//* 公共函数
import { Formats, Systems } from "@/assets/js";
//* 私有组件
import type ModuleSequence from "@/views/tool/components/sequence/index.vue";
//* 私有函数
import Example from "@/views/tool/components/sequence";

export const useComposable = (data: Ref<any>) => {
    //* 插件
    const i18n = useI18n();
    const route = useRoute();

    //* 静态变量
    const vars = {
        example: <Example | undefined>undefined,
        origin: <any>{},
        modes: [
            { name: "Pre-mRNA", value: "" },
            { name: i18n.t("CDNASequence"), value: "cDNA" },
            { name: i18n.t("AACodonSequence"), value: "codon" },
        ],
        inquires: [
            { label: i18n.t("Position"), value: "" },
            { label: i18n.t("Exon"), value: "exon" },
            { label: i18n.t("Mutation"), value: "variant" },
        ],
        //* 检索模式选项
        coordinate: <{ start?: number; end?: number }>{}, //* 检索坐标
    };

    //* 响应式变量
    const refs = {
        id: ref<string>(Formats.query(route.query.id) || data.value?.mrnas?.[0]?.value || ""), //* 转录本ID
        mode: ref<string>(Formats.query(route.query.mode)), //* 绘图模式
        copy: ref<string>(""), //* 复制
        exon: ref<string>(""),
        index: ref<string>(""), //* 检索索引
        exons: ref<UiTypes.candidate[]>([]),
        radius: ref<any[]>([]), //* 检索结果
        inquire: ref<string>(""), //* 检索类型
        keyword: ref<string>(""), //* 检索序列
        coordinate: ref<{ start?: number; end?: number }>({}), //* 检索坐标
        sequence: ref<any>(),
        variant: ref<string>(""), //* 突变位点检索内容
        range: ref<number[][]>([]),
    };

    //* 节点
    const nodes = {
        modal: ref<InstanceType<typeof UiModal>>(),
        header: ref<HTMLDivElement>(),
        select1: ref<InstanceType<typeof UiSelect>>(),
        select2: ref<InstanceType<typeof UiSelect>>(),
        select3: ref<InstanceType<typeof UiSelect>>(),
        sequence: ref<InstanceType<typeof ModuleSequence>>(),
        checker: ref<InstanceType<typeof UiFormItem>>(),
    };

    //* 函数列表
    const methods = {
        //* 页面跳转
        next: () => {
            open(routerResolve({ name: "BioVariantResult", query: { id: refs.id.value }, params: { id: route.params.id } }).href as string, "_blank");
        },

        //* 模式切换函数
        mode: (mode?: string) => {
            //* 数据更新
            refs.mode.value = mode || "";
            //* 检索类型是否存在
            if (!vars.inquires.find((value) => value.value == refs.inquire.value)?.value) refs.inquire.value = "";

            //* 渲染图形
            methods.init();
        },

        //* 数据获取
        data: (replace?: boolean) => {
            //* 数据获取
            $loading.open();
            api.frontend("/api/gene/mrna", {
                data: { id: route.params.id, mrna: refs.id.value },
                method: "POST",
                success: (response) => {
                    //* 数据缓存
                    vars.origin = response;
                    //* 页面初始化
                    methods.init(replace);
                },
            });
        },

        //* 初始化
        init: async (replace?: boolean) => {
            //* 更新加载器
            $loading.open({ message: i18n.t("UnderLoad") });

            //* 参数处理
            const sites = methods.site();
            const result = {
                size: Math.max(screen.height - (nodes.header.value?.offsetHeight || 0) - 264, 520),
                origin: vars.origin,
                message: i18n.t("UnderLoad"),
                mutations: <{ [name: string]: any }>{},
                radius: sites?.radius,
            };

            //* 数据初始化
            refs.copy.value = "";
            refs.exon.value = "";
            refs.range.value = [];
            refs.radius.value = [];
            refs.keyword.value = "";
            refs.variant.value = "";
            refs.coordinate.value.end = sites?.end;
            refs.coordinate.value.start = sites?.start;

            //* 校验状态更新
            nodes.checker.value?.hidden();

            //* 绘图
            nodes.sequence.value?.render(refs.mode.value, result, (data: any) => {
                refs.exons.value = data?.exons || [];
                refs.sequence.value = data;
            });

            //* 路由更新
            if (replace) {
                history.replaceState(
                    {},
                    "",
                    routerResolve({ name: "BioSequenceResult", query: { id: refs.id.value, mode: refs.mode.value }, params: { id: route.params.id } })
                        .href as string
                );
            } else {
                routerPush({
                    name: "BioSequenceResult",
                    query: { id: refs.id.value, mode: refs.mode.value },
                    params: { id: route.params.id },
                    replace: true,
                });
            }
        },

        //* 位置解析
        site: () => {
            if (!route.query.site) return {};
            else {
                try {
                    const result = JSON.parse(Formats.query(route.query.site));
                    const site1 = Number(result?.[0]);
                    const site2 = Number(result?.[1]);
                    if (isNaN(site1) || isNaN(site2) || site1 <= 0 || site2 <= 0) return;
                    else if (site1 <= site2) return { radius: [[site1, site2]], start: site1, end: site2 };
                    else {
                        return { radius: [[site2, site1]], start: site2, end: site1 };
                    }
                } catch {
                    return {};
                }
            }
        },

        //* 序列复制
        copy: () => Systems.copyText(refs.copy.value),

        //* 外显子区域检索
        exon: () => {
            if (refs.mode.value == "cDNA" || refs.mode.value == "codon") {
                const index = refs.exons.value.findIndex((value) => value.label == refs.exon.value);
                //* 更新画布
                nodes.sequence.value?.update({ site: refs.exons.value[index].value, area: refs.exon.value });
                //* 复制
                refs.copy.value = refs.sequence.value.sequence.slice(refs.exons.value[index].value - 1, refs.exons.value[index + 1].value - 1);
            } else {
                const exon = refs.exons.value.find((value) => value.label == refs.exon.value);
                //* 更新画布
                nodes.sequence.value?.update({ site: exon?.value, area: refs.exon.value });
                //* 复制
                refs.copy.value = refs.sequence.value.sequence.slice(exon?.radius[0], exon?.radius[1]);
            }
        },

        //* 隐藏下拉框窗口
        hidden: () => {
            nodes.select1.value?.hidden();
            nodes.select2.value?.hidden();
            nodes.select3.value?.hidden();
        },

        //* 序列检索
        sequence: () => {
            if (!refs.keyword.value) return;
            else {
                refs.keyword.value = refs.keyword.value.replace(/\s/g, "").toLocaleUpperCase();
                refs.radius.value = [];
            }

            //* 数据初始化
            const size = refs.keyword.value.length;
            const radius: number[][] = [];
            const sequence = nodes.sequence.value?.sequence() || "";

            //* 字符串长度检测
            if (refs.mode.value == "codon") {
                if (size > 100 || size < 1) return $message.error(i18n.t("SequenceSearchRangeError", { max: 100, min: 1 }));
            } else {
                if (size > 100 || size < 15) return $message.error(i18n.t("SequenceSearchRangeError", { max: 100, min: 15 }));
            }

            //* 创建正则表达式对象
            const regex = new RegExp(refs.keyword.value, "g");
            //* 使用正则表达式的exec方法循环查找所有匹配项
            let result;
            while ((result = regex.exec(sequence)) != null) {
                radius.push([result.index + 1, result.index + refs.keyword.value.length]);
            }

            //* 生成候选列表
            refs.index.value = radius?.[0]?.[0].toString();
            radius.forEach((range, index) => {
                refs.radius.value.push({
                    value: range[0],
                    label: `${index + 1} / ${radius.length}`,
                    name: `${index + 1} / ${radius.length} (${range[0]}, ${range[1]})`,
                    range: range,
                });
            });
            //* 对应的位置自动填写
            refs.coordinate.value = { start: radius?.[0]?.[0], end: radius?.[0]?.[1] };

            //* 检测是否重绘图像
            if (!radius.length) return $message.error(i18n.t("SequenceSearchError"));
            else {
                //* 序列状态更新
                nodes.sequence.value?.update({ radius: radius });
            }
        },

        //* 检索结果选择
        selector: () => {
            nodes.sequence.value?.move(Number(refs.index.value));

            //* 对应的位置自动填写
            const range = refs.radius.value.find((value: any) => value.value == refs.index.value).range;
            refs.coordinate.value = { start: range?.[0], end: range?.[1] };
        },

        //* 范围检索
        coordinate: () => {
            vars.coordinate = utility.cloneDeep(refs.coordinate.value);
            //* 序列状态更新
            if (refs.coordinate.value.end && refs.coordinate.value.start && refs.coordinate.value.end - refs.coordinate.value.start >= 0) {
                //* 序列字符串缓存
                const sequence = nodes.sequence.value?.sequence() || "";
                if (refs.mode.value == "cDNA" || refs.mode.value == "codon") {
                    if (refs.coordinate.value.start < 1) refs.coordinate.value.start = 1;
                    if (refs.coordinate.value.start > sequence.length) refs.coordinate.value.start = sequence.length;
                    if (refs.coordinate.value.end < 1) refs.coordinate.value.end = refs.coordinate.value.start;
                    if (refs.coordinate.value.end > sequence.length) refs.coordinate.value.end = sequence.length;
                } else {
                    const start: number = vars.origin.coordinate?.[0].start;
                    const end: number = vars.origin.coordinate?.slice(-1)[0].end;
                    if (refs.coordinate.value.start < start) refs.coordinate.value.start = start;
                    if (refs.coordinate.value.start > end) refs.coordinate.value.start = end;
                    if (refs.coordinate.value.end < start) refs.coordinate.value.end = refs.coordinate.value.start;
                    if (refs.coordinate.value.end > end) refs.coordinate.value.end = end;
                }
                nodes.sequence.value?.update({ radius: [[refs.coordinate.value.start, refs.coordinate.value.end]] });
                refs.copy.value = sequence.slice(refs.coordinate.value.start - 1, refs.coordinate.value.end) || "";
            } else {
                refs.coordinate.value.end = undefined;
                nodes.sequence.value?.update({ radius: [] });
                refs.copy.value = "";
            }
        },

        //* 范围检索清空
        cancel: () => {
            //* 数据格式化
            refs.radius.value = [];
            refs.coordinate.value.start = undefined;
            refs.coordinate.value.end = undefined;
            refs.exon.value = "";
            refs.copy.value = "";
            refs.variant.value = "";

            //* 序列状态更新
            if (JSON.stringify(refs.range.value) == JSON.stringify(nodes.sequence.value?.radius())) {
                nodes.sequence.value?.update({ radius: [], site: 0, area: "-1" });
            } else {
                nodes.sequence.value?.update({ radius: [], site: 0, area: "-1", info: true });
            }

            //* 校验状态更新
            nodes.checker.value?.hidden();
        },

        //* 预测函数
        prediction: (data: { cds: string; site: string; mode: "RNA-Splicer" | "Pathogenicity-Predictor" }) => {
            //* 接口请求
            $loading.open({ message: "" });
            api.frontend("/api/bio/variant/params", {
                method: "POST",
                data: {
                    id: route.params.id,
                    mode: data.mode,
                    type: refs.mode.value,
                    site: data.site,
                    nucleotide: data.cds,
                    transcript: refs.id.value,
                },
                finally: () => $loading.close(),
                success: (response) => {
                    if (data.mode == "RNA-Splicer") {
                        open(routerResolve({ name: "RnaSplicer", query: { uuid: response } }).href, "_blank");
                    } else {
                        open(routerResolve({ name: "Pathogenicity", query: { uuid: response } }).href, "_blank");
                    }
                },
            });
        },

        //* 下载序列
        download: () => {
            //* 数据处理
            const sequence = nodes.sequence.value?.sequence().replaceAll("-", "") || "";
            const cds = nodes.sequence.value?.cds().replaceAll("-", "") || "";

            let text: string[] = [];
            switch (refs.mode.value) {
                case "codon":
                    text = [`>${computeds.file.value.txt.title}\n`, "protein\n", sequence, "\n\n", "cds\n", cds];
                    break;
                case "cDNA":
                    text = [`>${computeds.file.value.txt.title}\n`, cds];
                    break;
                default:
                    text = [`>${computeds.file.value.txt.title}\n`, sequence];
                    break;
            }

            //* 生成txt文件
            const blob = new Blob(text, { type: "text/plain;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            Systems.download(url, computeds.file.value.txt.title + ".txt");
        },

        //* 检索突变函数
        variant: () => {
            $loading.open({ message: i18n.t("UnderLoad"), delay: 200 });
            api.frontend("/api/mutations/site", {
                method: "POST",
                hidden: true,
                data: {
                    id: route.params.id,
                    mode: refs.mode.value,
                    mrna: refs.id.value,
                    keyword: refs.variant.value.trim(),
                },
                finally: () => $loading.close(),
                error: (error) => {
                    //* 数据更新
                    refs.range.value = [];
                    //* 状态更新
                    nodes.sequence.value?.update({ radius: [] });
                    nodes.checker.value?.show(error.message, "error");
                },
                success: (response) => {
                    refs.range.value = response.range;
                    //* 状态更新
                    nodes.sequence.value?.update({ radius: response.range, tips: true });
                    nodes.checker.value?.hidden();
                },
            });
        },
    };

    //* 属性列表
    const binds = reactive({
        //* 检索模式
        inquire: computed(() => {
            return {
                "width": i18n.locale.value == "zh" ? 120 : 120,
                "z-index": 67,
                "candidates": vars.inquires,
            };
        }),

        //* 序列输入框
        sequence: computed(() => {
            const min = refs.mode.value == "codon" ? 1 : 15;
            return {
                placeholder: i18n.t("PleaseEnterRangeSequence", { max: 100, min: min }),
            };
        }),

        //* 检索结果选择列表
        selector: computed(() => {
            return {
                "width": 112,
                "z-index": 67,
                "readonly": !refs.radius.value.length,
                "candidates": refs.radius.value,
            };
        }),

        //* 坐标输入框
        coordinate: {
            placeholder: { start: i18n.t("Begin"), end: i18n.t("End", 2) },
        },
    });

    //* 计算属性
    const computeds = {
        exons: computed(() => {
            const context = `${refs.exon.value}`.replace(/\s/g, "").toLocaleLowerCase();
            return refs.exons.value.filter((exon) => {
                return `${exon.label}`.replace(/\s/g, "").toLocaleLowerCase().includes(context);
            });
        }),

        file: computed(() => {
            const title = `${useSpeciesName(data.value.specie)}-${data.value.name}-${refs.id.value}`;
            const mrna = data.value?.mrnas?.find((value: any) => value.value == refs.id.value);
            switch (refs.mode.value) {
                case "cDNA":
                    return {
                        txt: { title: `${title}-CDS` },
                        gb: { title: `${title}-mRNA`, href: mrna.transcript_gb_file },
                    };
                case "codon":
                    return {
                        txt: { title: `${title}-Protein` },
                        gb: { title: `${title}-Protein`, href: mrna.protein_gb_file },
                    };
                default:
                    return {
                        txt: { title: `${title}-mRNA` },
                        gb: { title: `${title}-mRNA`, href: mrna.transcript_gb_file },
                    };
            }
        }),
    };

    return { vars, refs, nodes, binds, methods, computeds };
};
